'use client';
import React from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'next/navigation';
import { CtaButton, IMAGE_SIZES, ResponsiveImage } from '@web/molecules';
import { Divider, Eyebrow } from '@web/atoms';
import { useReporter } from '../../reporter';
import { BoxedCountdownClock } from '../BoxedCountdownClock';
import { SocialButton } from '../SocialButton';
import { EmailCapture } from '../EmailCaptureBar/EmailCapture';
import { CTAGroup } from '../CTAGroup';
import { orientationSettings } from './constants';
import { heroButtonClicked, appStoreCTAButtonClicked } from './events';
import {
  StyledHero,
  BackgroundImage,
  ContentContainer,
  Content,
  Header,
  Subheader,
  HeroCtaShell,
  CountdownAndLink,
  InputWrapper,
  SocialButtonWrapper,
  DividerContainer,
  Footer,
} from './styles.js';

export const Hero = ({
  headline,
  headlineVariants,
  subheadline,
  cta,
  image,
  backgroundColor = 'background.base',
  fontColor = 'text.primary',
  horizontalOrientation = 'left',
  targetDate = null,
  eyebrow,
  socialButtons,
  divider = false,
  footer,
  typography = 'heading3',
}) => {
  const reporter = useReporter();
  const searchParams = useSearchParams();
  const headlineVariant = searchParams?.get('hpkeyword');

  if (headlineVariant && headlineVariants) {
    const variant = headlineVariants.find(e => e.variant === headlineVariant);
    if (variant) {
      headline = variant.heading;
    }
  }

  return (
    <StyledHero backgroundColor={backgroundColor}>
      <BackgroundImage image={image}>
        <ResponsiveImage
          {...image}
          priority
          objectFit={image.picture ? 'cover' : undefined}
          sizes={IMAGE_SIZES.fullBleed}
        />
      </BackgroundImage>
      <ContentContainer
        image={image}
        horizontalOrientation={orientationSettings[horizontalOrientation]}
      >
        <Content
          horizontalOrientation={orientationSettings[horizontalOrientation]}
        >
          {eyebrow && <Eyebrow fontColor={fontColor}>{eyebrow}</Eyebrow>}
          {headline && (
            <Header $fontColor={fontColor} $typography={typography}>
              {headline}
            </Header>
          )}
          {subheadline && (
            <Subheader $fontColor={fontColor}>{subheadline}</Subheader>
          )}
          {(targetDate || cta) && (
            <CountdownAndLink>
              {cta && cta.component === 'email_capture' ? (
                <InputWrapper>
                  <EmailCapture {...cta} buttonVariant={cta.variant} />
                </InputWrapper>
              ) : cta && cta.component === 'cta_group' ? (
                <CTAGroup {...cta} />
              ) : (
                cta && (
                  <HeroCtaShell
                    fullWidth={cta.fullWidth}
                    fullWidthMobile={cta.fullWidthMobile}
                    fullWidthDesktop={cta.fullWidthDesktop}
                  >
                    <CtaButton
                      {...cta}
                      href={cta.target}
                      variant={cta.variant}
                      icon={cta.icon}
                      iconAlignment={cta.iconAlignment}
                      headerText={cta.headerText}
                      onClick={() =>
                        reporter.tag(heroButtonClicked(cta.text, cta.target))
                      }
                    >
                      {cta.text}
                    </CtaButton>
                  </HeroCtaShell>
                )
              )}
              {Boolean(targetDate) && (
                <BoxedCountdownClock
                  targetDate={targetDate}
                  textColor={fontColor}
                />
              )}
            </CountdownAndLink>
          )}
          {Boolean(socialButtons?.length) && (
            <SocialButtonWrapper>
              {socialButtons.map((button, key) => (
                <SocialButton
                  key={key}
                  {...button}
                  onClick={() => reporter.tag(appStoreCTAButtonClicked())}
                />
              ))}
            </SocialButtonWrapper>
          )}
          {divider && (
            <DividerContainer>
              <Divider direction="horizontal" color="light" />
            </DividerContainer>
          )}
          {footer && <Footer $fontColor={fontColor}>{footer}</Footer>}
        </Content>
      </ContentContainer>
    </StyledHero>
  );
};

Hero.displayName = 'Hero';

Hero.propTypes = {
  headline: PropTypes.node,
  headlineVariants: PropTypes.array,
  subheadline: PropTypes.string,
  cta: PropTypes.object,
  backgroundColor: PropTypes.string,
  fontColor: PropTypes.string,
  image: PropTypes.object.isRequired,
  horizontalOrientation: PropTypes.oneOf(['left', 'center', 'right']),
  targetDate: PropTypes.instanceOf(Date), //eslint-disable-line react/forbid-prop-types,
  eyebrow: PropTypes.string,
  socialButtons: PropTypes.array,
  divider: PropTypes.bool,
  footer: PropTypes.element,
  typography: PropTypes.string,
};
